import React, { useEffect, useState } from "react";
import { Card, CircularProgress, Icon, Dialog, DialogTitle, DialogContent, DialogActions, Grid, InputLabel, Chip, Tooltip } from "@mui/material";
import { toDateTime } from "utils/DateUtils";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSelect from "components/MDSelect";
import MDInput from "components/MDInput";
import { get, post, postNoResponse } from "utils/WebService";
import { useMaterialUIController } from "context";

import { ESCURO, coresDoTema } from "assets/coresTema";

export default function HeaderDevedor({ recordId, selectedFicha: activeFichaContext, onSelectFicha, onNavigateToDevedor, onOpenDadosCarteira }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const showTimer = true;

    const [devedor, setDevedor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dataAberturaFicha, setDataAberturaFicha] = useState(null);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [pausaState, setPausaState] = useState({ emPausa: false });
    const [collapsedGroups, setCollapsedGroups] = useState({});

    const toggleGroup = (groupName) => {
        setCollapsedGroups(prev => ({
            ...prev,
            [groupName]: prev[groupName] === false ? true : false
        }));
    };

    const reloadDevedor = () => {
        if (!recordId) return;
        get(`/devedor/by-id/${recordId}`).then((dataDevedor) => {
            setDevedor(dataDevedor);
        }).catch(err => {
            console.error("Erro ao recarregar HeaderDevedor", err);
        });
    };

    const fetchPausaState = () => {
        get(`/users/pausa?t=${new Date().getTime()}`).then(res => {
            setPausaState(res);
        }).catch(err => console.error("Erro ao buscar estado de pausa", err));
    };

    useEffect(() => {
        if (!recordId) return;

        setLoading(true);
        get(`/devedor/by-id/${recordId}`).then((dataDevedor) => {
            setDevedor(dataDevedor);
            setLoading(false);
        }).catch(err => {
            console.error("Erro ao carregar HeaderDevedor", err);
            setLoading(false);
        });

        // Fetch synchronized start time from backend
        get(`/users/devedor/${recordId}/atividade`).then((res) => {
            if (res) {
                setDataAberturaFicha(new Date(res));
            } else {
                setDataAberturaFicha(new Date());
            }
        }).catch(() => {
            setDataAberturaFicha(new Date());
        });

        const handlePausaToggled = (e) => {
            if (e.detail && typeof e.detail.emPausa !== 'undefined') {
                setPausaState(e.detail);
            } else {
                fetchPausaState();
            }
        };
        window.addEventListener("devedorStatusUpdated", reloadDevedor);
        window.addEventListener("pausaToggled", handlePausaToggled);
        
        fetchPausaState();

        return () => {
            window.removeEventListener("devedorStatusUpdated", reloadDevedor);
            window.removeEventListener("pausaToggled", handlePausaToggled);
        };
    }, [recordId]);

    // Timer loop updates currentTime every second
    useEffect(() => {
        const timerId = setInterval(() => {
            if (!pausaState.emPausa) {
                setCurrentTime(new Date());
            }
        }, 1000);

        return () => clearInterval(timerId);
    }, [pausaState.emPausa]);

    const formatTimer = () => {
        if (!dataAberturaFicha) return "00:00";
        const diffMs = currentTime.getTime() - dataAberturaFicha.getTime();
        const totalSeconds = Math.max(0, Math.floor(diffMs / 1000));
        
        const hrs = Math.floor(totalSeconds / 3600);
        const mins = Math.floor((totalSeconds % 3600) / 60);
        const secs = totalSeconds % 60;
        const pad = (val) => String(val).padStart(2, "0");
        if (hrs > 0) {
            return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
        }
        return `${pad(mins)}:${pad(secs)}`;
    };

    const renderTermometro = () => {
        if (!devedor) return null;
        
        let forca = devedor.statusFichaForca || 0;
        let color = "#9e9e9e"; // cinza (neutro)
        let fillY = 20;

        if (forca === 1) {
            color = "#03a9f4"; // azul (frio)
            fillY = 16;
        } else if (forca === 2) {
            color = "#ff9800"; // laranja (morno)
            fillY = 11;
        } else if (forca === 3) {
            color = "#f44336"; // vermelho (quente)
            fillY = 6;
        }

        const outlineColor = darkMode ? "#dddddd" : "#212121";

        const svgIcon = (
            <svg width="24" height="32" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Fundo vazio da coluna */}
                <path d="M6.5 21.5 V 6 A 1.5 1.5 0 0 1 9.5 6 V 21.5 Z" fill={darkMode ? ESCURO.painel : "#e0e0e0"} />
                
                {/* Preenchimento colorido (Mercúrio) */}
                <rect x="6.5" y={fillY} width="3" height={21.5 - fillY} fill={color} />
                
                {/* Bulbo colorido */}
                <circle cx="8" cy="25" r="3.25" fill={color} />
                
                {/* Reflexo / Brilho no bulbo para dar volume */}
                <path d="M9.5 23.5 A 1.5 1.5 0 0 1 8.5 26.5" stroke="white" strokeWidth="1" strokeLinecap="round" opacity="0.6"/>

                {/* Contorno externo */}
                <path d="M6 21.5 A 4 4 0 1 0 10 21.5 V 6 A 2 2 0 0 0 6 6 Z" stroke={outlineColor} strokeWidth="1.5" fill="none" />
                
                {/* Tracinhos (Escala) */}
                <path d="M13 6h5 M13 9h3 M13 12h5 M13 15h3 M13 18h5 M13 21h3" stroke={outlineColor} strokeWidth="1.5" strokeLinecap="round" />
            </svg>
        );

        return (
            <MDBox display="flex" alignItems="center" ml={2} sx={{ cursor: "help" }} title={`Força: ${forca === 3 ? 'Forte' : forca === 2 ? 'Médio' : forca === 1 ? 'Fraco' : 'Neutro'} | Status atual: ${devedor.statusFichaNome || 'Desconhecido'}`}>
                {svgIcon}
            </MDBox>
        );
    };

    if (loading || !devedor) return <MDBox p={1} display="flex" justifyContent="center"><CircularProgress size={20} /></MDBox>;

    // Bloco da carteira (pedido da Aline em 26/10/2026): com uma ficha só, tudo na linha do bloco, sem expandir (ficha,
    // status, responsável e campanha; clicar seleciona a ficha); com mais de uma, o bloco expande para mostrar cada ficha
    // e o cabeçalho mostra o status e o responsável da ficha selecionada nele.
    const cores = coresDoTema(darkMode);
    const ehSelecionada = (f) => !!activeFichaContext && !!(
        (activeFichaContext.rawId && f.rawId && String(activeFichaContext.rawId) === String(f.rawId)) ||
        (activeFichaContext.id && f.id && activeFichaContext.id === f.id)
    );

    // Etiquetas e textos de uma ficha: na lista do bloco expandido (com a carteira no rótulo) e no bloco de ficha única
    const conteudoDaFicha = (f, comCarteira) => {
        const carteiraStr = f.nomeCarteira || f.carteiraNome || (f.isHonorario ? "Honorários" : "Carteira");
        const statusStr = f.statusFichaNome || f.nomeStatusFicha || "Sem Status";
        const respStr = f.nomeUsuarioDono || f.nomeUsuario || "Sem Responsável";
        const isHon = f.isHonorario === true;
        const isDev = f.situacao === 1;
        const isQuitada = f.situacao === 7;
        const campStr = f.nomeCampanha || devedor.nomeCampanha;
        const enqStr = f.enquadramentoCampanha || devedor.enquadramentoCampanha;
        const fichaIdLabel = f.rawId ? `Ficha #${f.rawId}` : "";
        const isSelected = ehSelecionada(f);
        const pertenceMinhaEquipe = f.pertenceAMinhaEquipe !== false;
        const rotulo = comCarteira
            ? `[${fichaIdLabel ? `${fichaIdLabel} - ` : ""}${carteiraStr}]:`
            : `${fichaIdLabel || carteiraStr}:`;

        return (
            <>
                {isSelected && (
                    <Chip label="SELECIONADA" color="primary" size="small" sx={{ height: "18px", fontSize: "9px", fontWeight: "bold", mr: 0.3 }} />
                )}
                {isHon && (
                    <Chip label="HO" color="warning" size="small" sx={{ height: "18px", fontSize: "9px", fontWeight: "bold", mr: 0.3 }} />
                )}
                {!pertenceMinhaEquipe && (
                    <Chip label="FORA DA EQUIPE" color="warning" size="small" sx={{ height: "18px", fontSize: "9px", fontWeight: "bold", mr: 0.3 }} />
                )}
                <MDTypography variant="caption" fontWeight="bold" sx={{ color: isDev ? "#f44336" : (isQuitada ? (darkMode ? "#ce93d8" : "#8e24aa") : (isHon ? "#ff8f00" : (darkMode ? "#90caf9" : "#1976d2"))), fontSize: "11px" }}>
                    {rotulo}
                </MDTypography>
                {isDev && (
                    <Tooltip
                        title={
                            f.dataDevolucao
                                ? `Devolvida em ${toDateTime(f.dataDevolucao)}${f.nomeUsuarioDevolucao ? ` por ${f.nomeUsuarioDevolucao}` : ""}`
                                : "Ficha Devolvida"
                        }
                        arrow
                    >
                        <Chip
                            label="DEVOLVIDA"
                            color="error"
                            size="small"
                            sx={{ height: "18px", fontSize: "9px", fontWeight: "bold", mr: 0.5, cursor: "help" }}
                        />
                    </Tooltip>
                )}
                {isQuitada && (
                    <Chip label="QUITADA" size="small" sx={{ height: "18px", fontSize: "9px", fontWeight: "bold", mr: 0.5, bgcolor: "#7b1fa2", color: "#fff" }} />
                )}
                <MDTypography variant="caption" fontWeight="medium" sx={{ fontSize: "11px", color: darkMode ? "#fff" : "#333" }}>
                    Status: <strong>{statusStr}</strong> | Resp: <strong>{respStr}</strong>
                </MDTypography>
                {campStr && !isHon && (
                    <MDBox
                        component="span"
                        sx={{
                            ml: 0.5,
                            pl: 0.8,
                            borderLeft: `1px solid ${darkMode ? ESCURO.borda : "#cfd8dc"}`,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.4
                        }}
                    >
                        <Icon fontSize="inherit" sx={{ fontSize: "13px !important", color: darkMode ? "#81c784" : "#2e7d32" }}>campaign</Icon>
                        <MDTypography variant="caption" sx={{ fontSize: "11px", color: darkMode ? "#81c784" : "#2e7d32", fontWeight: "bold" }}>
                            Campanha: {campStr}
                        </MDTypography>
                        {enqStr && (
                            <MDTypography variant="caption" sx={{ fontSize: "11px", color: darkMode ? "#b0bec5" : "#607d8b", fontWeight: "medium" }}>
                                ({enqStr})
                            </MDTypography>
                        )}
                    </MDBox>
                )}
            </>
        );
    };

    return (
        <Card sx={{ 
            p: 1, 
            px: 2, 
            backgroundColor: darkMode ? ESCURO.cartao : "#f8f9fa", 
            border: `1px solid ${darkMode ? ESCURO.divisoria : "#e0e0e0"}`, 
            boxShadow: "none" 
        }}>
            <MDBox display="flex" flexDirection="column" width="100%">
                <MDBox display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                        <Icon sx={{ mr: 0.5, color: "#f4c22b", fontSize: "24px !important" }}>attachment</Icon>
                        <MDTypography variant="body2" fontWeight="medium" color="text">
                            {devedor.unencryptedId ? `${devedor.unencryptedId} - ` : ""}{devedor.nome || "Não informado"}
                        </MDTypography>
                        {devedor.idDevedorPai && (
                            <MDBox
                                component="span"
                                sx={{
                                    px: 1,
                                    py: 0.2,
                                    borderRadius: "4px",
                                    bgcolor: "#ff9800",
                                    color: "#fff",
                                    fontSize: "10px",
                                    fontWeight: "bold",
                                    letterSpacing: "0.5px"
                                }}
                            >
                                EMBARGOS DE TERCEIRO (HONORÁRIO)
                            </MDBox>
                        )}
                        {devedor.devedoresFilhos && devedor.devedoresFilhos.length > 0 && (
                            <MDBox
                                component="span"
                                sx={{
                                    px: 1,
                                    py: 0.2,
                                    borderRadius: "4px",
                                    bgcolor: "#0288d1",
                                    color: "#fff",
                                    fontSize: "10px",
                                    fontWeight: "bold",
                                    letterSpacing: "0.5px"
                                }}
                            >
                                {devedor.devedoresFilhos.length} TERCEIRO(S) VINCULADO(S)
                            </MDBox>
                        )}
                        {renderTermometro()}
                    </MDBox>
                    {showTimer && (
                        <MDBox display="flex" alignItems="center" gap={2}>
                            <MDBox display="flex" alignItems="center" sx={{ bgcolor: darkMode ? "#1a2a3a" : "#e3f2fd", px: 1.5, py: 0.5, borderRadius: "8px" }}>
                                <Icon sx={{ color: pausaState.emPausa ? "#ff9800" : "#1976d2", fontSize: "16px !important", mr: 0.5 }}>
                                    {pausaState.emPausa ? "pause_circle" : "timer"}
                                </Icon>
                                <MDTypography variant="caption" fontWeight="bold" color={pausaState.emPausa ? "warning" : "info"} sx={{ fontFamily: "monospace", fontSize: "12px" }}>
                                    Tempo em atendimento: {formatTimer()} {pausaState.emPausa ? "(EM PAUSA)" : ""}
                                </MDTypography>
                            </MDBox>
                        </MDBox>
                    )}
                </MDBox>
                {(() => {
                    const allFichas = devedor.fichas || [];
                    const totalFichasDevedor = allFichas.length;

                    if (totalFichasDevedor > 0) {
                        // Agrupa fichas por Carteira/Categoria
                        const groups = {};
                        allFichas.forEach(f => {
                            const groupName = f.nomeCarteira || f.carteiraNome || (f.isHonorario ? "Honorários" : "Carteira Principal");
                            if (!groups[groupName]) {
                                groups[groupName] = [];
                            }
                            groups[groupName].push(f);
                        });

                        const groupKeys = Object.keys(groups);

                        return (
                            <MDBox display="flex" flexDirection="column" gap={1} mt={1.2} width="100%">
                                {groupKeys.map(groupName => {
                                    const groupFichas = groups[groupName];
                                    // Uma ficha só: tudo na linha do bloco, sem expandir
                                    const fichaUnica = groupFichas.length === 1 ? groupFichas[0] : null;
                                    const unicaForaDaEquipe = !!fichaUnica && fichaUnica.pertenceAMinhaEquipe === false;
                                    const unicaSelecionavel = !!fichaUnica && !unicaForaDaEquipe && !ehSelecionada(fichaUnica);
                                    const isGroupCollapsed = collapsedGroups[groupName] !== false;
                                    const hasSelectedInGroup = groupFichas.some(ehSelecionada);
                                    const fichaNoCabecalho = groupFichas.find(ehSelecionada);
                                    const cabecalhoClicavel = !fichaUnica || unicaSelecionavel;

                                    return (
                                        <MDBox
                                            key={groupName}
                                            sx={{
                                                borderRadius: "6px",
                                                border: `1px solid ${darkMode ? "rgba(255,255,255,0.18)" : "#cfd8dc"}`,
                                                bgcolor: darkMode ? ESCURO.painel : "#fafafa",
                                                overflow: "hidden"
                                            }}
                                        >
                                            {/* Cabeçalho do bloco: expande (várias fichas) ou seleciona a ficha (ficha única) */}
                                            <MDBox
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                px={1.5}
                                                py={0.6}
                                                onClick={() => {
                                                    if (!fichaUnica) toggleGroup(groupName);
                                                    else if (unicaSelecionavel && onSelectFicha) onSelectFicha(fichaUnica);
                                                }}
                                                title={unicaForaDaEquipe
                                                    ? "Acesso restrito: você não pertence à equipe desta carteira"
                                                    : (unicaSelecionavel ? "Clique para selecionar esta ficha para visualização" : undefined)}
                                                sx={{
                                                    cursor: cabecalhoClicavel ? "pointer" : (unicaForaDaEquipe ? "not-allowed" : "default"),
                                                    bgcolor: darkMode ? ESCURO.cartao : "#eef2f6",
                                                    "&:hover": cabecalhoClicavel ? { bgcolor: darkMode ? ESCURO.campo : "#e2e8f0" } : {},
                                                    opacity: unicaForaDaEquipe ? 0.65 : 1,
                                                    transition: "all 0.2s ease"
                                                }}
                                            >
                                                <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                                                    <Icon sx={{ fontSize: "18px !important", color: darkMode ? "#90caf9" : "#1976d2" }}>
                                                        folder
                                                    </Icon>
                                                    <MDTypography variant="button" fontWeight="bold" sx={{ fontSize: "12px", color: darkMode ? "#ffffff" : "#333333" }}>
                                                        {groupName}
                                                    </MDTypography>
                                                    {fichaUnica ? (
                                                        <>
                                                            {/* Separador; os itens da ficha quebram de linha um a um, sem descer todos juntos */}
                                                            <MDBox component="span" sx={{ alignSelf: "stretch", minHeight: 18, borderLeft: `1px solid ${cores.borda}` }} />
                                                            {conteudoDaFicha(fichaUnica, false)}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Chip
                                                                label={`${groupFichas.length} ficha(s)`}
                                                                size="small"
                                                                color={hasSelectedInGroup ? "info" : "default"}
                                                                sx={{
                                                                    height: "18px",
                                                                    fontSize: "10px",
                                                                    fontWeight: "bold",
                                                                    bgcolor: darkMode ? (hasSelectedInGroup ? undefined : "rgba(255,255,255,0.2)") : undefined,
                                                                    color: darkMode ? "#ffffff !important" : undefined
                                                                }}
                                                            />
                                                            {hasSelectedInGroup && (
                                                                <Chip
                                                                    label="SELEÇÃO ATIVA NESTE BLOCO"
                                                                    color="primary"
                                                                    size="small"
                                                                    sx={{ height: "18px", fontSize: "9px", fontWeight: "bold" }}
                                                                />
                                                            )}
                                                            {fichaNoCabecalho && (
                                                                <MDTypography
                                                                    variant="caption"
                                                                    sx={{ fontSize: "12px", color: cores.textoSecundario, pl: 1, borderLeft: `1px solid ${cores.borda}` }}
                                                                    title={fichaNoCabecalho.rawId ? `Ficha #${fichaNoCabecalho.rawId}` : undefined}
                                                                >
                                                                    Status: <MDBox component="strong" sx={{ color: cores.texto }}>{fichaNoCabecalho.statusFichaNome || fichaNoCabecalho.nomeStatusFicha || "Sem Status"}</MDBox>
                                                                    {" | "}Resp: <MDBox component="strong" sx={{ color: cores.texto }}>{fichaNoCabecalho.nomeUsuarioDono || fichaNoCabecalho.nomeUsuario || "Sem Responsável"}</MDBox>
                                                                </MDTypography>
                                                            )}
                                                        </>
                                                    )}
                                                </MDBox>

                                                {!fichaUnica && (
                                                    <MDBox display="flex" alignItems="center" gap={0.5}>
                                                        <MDTypography variant="caption" sx={{ fontSize: "10.5px", color: darkMode ? "#e0e0e0" : "#666666", fontWeight: "medium" }}>
                                                            {isGroupCollapsed ? "Expandir" : "Recolher"}
                                                        </MDTypography>
                                                        <Icon sx={{ color: darkMode ? "#e0e0e0" : "#666666", fontSize: "20px !important" }}>
                                                            {isGroupCollapsed ? "keyboard_arrow_down" : "keyboard_arrow_up"}
                                                        </Icon>
                                                    </MDBox>
                                                )}
                                            </MDBox>

                                            {/* Fichas do bloco (só com mais de uma) */}
                                            {!fichaUnica && !isGroupCollapsed && (
                                                <MDBox display="flex" flexWrap="wrap" gap={1} p={1} alignItems="center" sx={{ bgcolor: darkMode ? ESCURO.painelEscuro : "transparent" }}>
                                                    {groupFichas.map((f, idx) => {
                                                        const isHon = f.isHonorario === true;
                                                        const isDev = f.situacao === 1;
                                                        const isQuitada = f.situacao === 7;
                                                        const isSelected = ehSelecionada(f);
                                                        const pertenceMinhaEquipe = f.pertenceAMinhaEquipe !== false;

                                                        return (
                                                            <MDBox
                                                                key={f.rawId || f.id || idx}
                                                                onClick={() => {
                                                                    if (pertenceMinhaEquipe && onSelectFicha) onSelectFicha(f);
                                                                }}
                                                                sx={{
                                                                    px: 1.2,
                                                                    py: 0.5,
                                                                    borderRadius: "6px",
                                                                    bgcolor: isSelected
                                                                        ? (darkMode ? "#1e3a5f" : "#e3f2fd")
                                                                        : (isDev
                                                                            ? (darkMode ? "#4a2525" : "#ffebee")
                                                                            : (isQuitada
                                                                                ? (darkMode ? "#3a1e4a" : "#f3e5f5")
                                                                                : (isHon ? (darkMode ? "#4a3b1a" : "#fff8e1") : (darkMode ? ESCURO.cartao : "#ffffff")))),
                                                                    border: isSelected
                                                                        ? "2px solid #1976d2"
                                                                        : `1px solid ${isDev ? "#ef5350" : (isQuitada ? "#ab47bc" : (isHon ? "#ffa000" : (darkMode ? ESCURO.borda : "#cfd8dc")))}`,
                                                                    boxShadow: isSelected
                                                                        ? (darkMode ? "0 0 0 2px #42a5f5, 0 2px 8px rgba(0,0,0,0.5)" : "0 0 0 1px #1976d2, 0 2px 8px rgba(25,118,210,0.25)")
                                                                        : "none",
                                                                    cursor: pertenceMinhaEquipe ? "pointer" : "not-allowed",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: 0.8,
                                                                    opacity: pertenceMinhaEquipe ? 1 : 0.65,
                                                                    transition: "all 0.2s ease"
                                                                }}
                                                                title={pertenceMinhaEquipe ? "Clique para selecionar esta ficha para visualização" : "Acesso restrito: você não pertence à equipe desta carteira"}
                                                            >
                                                                {conteudoDaFicha(f, true)}
                                                            </MDBox>
                                                        );
                                                    })}
                                                </MDBox>
                                            )}
                                        </MDBox>
                                    );
                                })}
                            </MDBox>
                        );
                    }
                    const campStrFinal = devedor.nomeCampanha || (allFichas.length === 1 ? allFichas[0]?.nomeCampanha : null);
                    const enqStrFinal = devedor.enquadramentoCampanha || (allFichas.length === 1 ? allFichas[0]?.enquadramentoCampanha : null);
                    if (campStrFinal) {
                        return (
                            <MDBox display="flex" flexWrap="wrap" gap={1} mt={1}>
                                <MDBox 
                                    sx={{
                                        px: 1.2,
                                        py: 0.4,
                                        borderRadius: "6px",
                                        bgcolor: darkMode ? ESCURO.painelEscuro : "#eef2f6",
                                        border: `1px solid ${darkMode ? ESCURO.borda : "#cfd8dc"}`,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.8
                                    }}
                                >
                                    <Icon fontSize="inherit" sx={{ fontSize: "14px !important", color: darkMode ? "#81c784" : "#2e7d32" }}>campaign</Icon>
                                    <MDTypography variant="caption" sx={{ fontSize: "11px", color: darkMode ? "#81c784" : "#2e7d32", fontWeight: "bold" }}>
                                        Campanha: {campStrFinal}
                                    </MDTypography>
                                    {enqStrFinal && (
                                        <MDTypography variant="caption" sx={{ fontSize: "11px", color: darkMode ? "#b0bec5" : "#607d8b", fontWeight: "medium" }}>
                                            ({enqStrFinal})
                                        </MDTypography>
                                    )}
                                </MDBox>
                            </MDBox>
                        );
                    }
                    return null;
                })()}
            </MDBox>
        </Card>
    );
}

